'use client'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaLock, FaUser } from 'react-icons/fa'
import { MdMailOutline } from 'react-icons/md'

import { Button } from '@/blocks/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/blocks/components/ui/form'
import { Input } from '@/blocks/components/ui/input'

import TurnstileCaptcha from './TurnstileCaptcha'
import useRegisterForm from './useRegisterForm'

const fieldClass =
  'rounded-md border-0 p-0 ps-4 text-base shadow-none placeholder:text-base placeholder:font-semibold placeholder:text-black focus-visible:ring-0 sm:text-2xl sm:placeholder:text-2xl'
const wrapperClass =
  'flex items-center rounded-[20px] bg-white p-4 shadow-md focus-visible:ring-1 focus-visible:ring-ring'

const RegisterSection = () => {
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const resetCaptcha = useCallback(() => setCaptchaResetKey(key => key + 1), [])
  const { form, handleSubmit } = useRegisterForm({ onCaptchaReset: resetCaptcha })
  const { t } = useTranslation()
  const handleCaptchaVerify = useCallback(
    (token: string) => {
      form.setValue('captchaToken', token, { shouldDirty: true, shouldValidate: true })
      form.clearErrors('captchaToken')
    },
    [form]
  )
  const clearCaptchaToken = useCallback(() => {
    form.setValue('captchaToken', '', { shouldDirty: true, shouldValidate: true })
  }, [form])

  return (
    <section className='mt-8'>
      <div className='container space-y-8 py-6 text-center'>
        <h2 className='mt-8 font-semibold text-white drop-shadow-lg max-sm:text-[26px]'>{t('register.title')}</h2>
        <Form {...form}>
          <form className='mx-auto max-w-[1000px] space-y-8' onSubmit={handleSubmit}>
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <FormItem>
                  <div className={wrapperClass}>
                    <FaUser className='size-6' />
                    <FormControl className='w-full'>
                      <Input className={fieldClass} {...field} placeholder={t('register.firstName.placeholder')} />
                    </FormControl>
                  </div>
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <FormItem>
                  <div className={wrapperClass}>
                    <FaUser className='size-6' />
                    <FormControl className='w-full'>
                      <Input className={fieldClass} {...field} placeholder={t('register.lastName.placeholder')} />
                    </FormControl>
                  </div>
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <div className={wrapperClass}>
                    <MdMailOutline className='size-8' />
                    <FormControl className='w-full'>
                      <Input
                        className={fieldClass}
                        type='email'
                        {...field}
                        placeholder={t('register.email.placeholder')}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <div className={wrapperClass}>
                    <FaLock className='size-6' />
                    <FormControl className='w-full'>
                      <Input
                        className={fieldClass}
                        type='password'
                        {...field}
                        placeholder={t('register.password.placeholder')}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <div className={wrapperClass}>
                    <FaLock className='size-6' />
                    <FormControl className='w-full'>
                      <Input
                        className={fieldClass}
                        type='password'
                        {...field}
                        placeholder={t('register.confirmPassword.placeholder')}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='captchaToken'
              render={() => (
                <FormItem>
                  <TurnstileCaptcha
                    resetKey={captchaResetKey}
                    onVerify={handleCaptchaVerify}
                    onExpire={clearCaptchaToken}
                    onError={clearCaptchaToken}
                  />
                  <FormMessage className='text-start text-sm sm:text-lg' />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className='h-14 w-full rounded-[20px] bg-primary-light px-6 py-4 text-lg text-white hover:bg-lime-300 sm:h-[84px] sm:px-10 sm:py-6 sm:text-3xl'
            >
              {form.formState.isSubmitting ? <Loader2 className='size-8 animate-spin' /> : t('register.button.submit')}
            </Button>
          </form>
        </Form>
      </div>
    </section>
  )
}

export default RegisterSection
